/** Adapter between the API subscription (raw Prisma) and the UI `Subscription`. */

import type { Currency, SubFrequency, SubStatus, Subscription } from "@/types";

export interface ApiSubscription {
  id: string;
  name: string;
  provider: string;
  cost: number | string;
  currency?: Currency;
  frequency: SubFrequency;
  renewalDate?: string | null;
  status: SubStatus;
}

export interface SubscriptionInput {
  name: string;
  provider: string;
  cost: number;
  /**
   * Devise de l'abonnement. Omise a la creation (USD par defaut) ; a la
   * MODIFICATION on renvoie la devise courante, sinon un abonnement en DZD
   * repassait silencieusement en USD.
   */
  currency?: Currency;
  frequency: SubFrequency;
  renewalDate?: string;
  /** Default paying treasury account for renewals (D3). */
  accountId?: string;
  /**
   * Omis à la création (le défaut ACTIVE s'applique). À la MODIFICATION on
   * renvoie le statut courant : sans cela, éditer un abonnement suspendu le
   * réactivait silencieusement.
   */
  status?: SubStatus;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/** ISO datetime → "YYYY-MM-DD". */
function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toSubscription(api: ApiSubscription): Subscription {
  return {
    id: api.id,
    name: api.name,
    provider: api.provider,
    cost: toNumber(api.cost),
    currency: api.currency ?? "USD",
    frequency: api.frequency,
    renewalDate: formatDate(api.renewalDate),
    status: api.status,
  };
}

export function toSubscriptionWriteBody(input: SubscriptionInput): Record<string, unknown> {
  return {
    name: input.name,
    provider: input.provider,
    cost: input.cost,
    // La devise saisie doit être respectée : la coder en dur écrasait la devise
    // réelle de l'abonnement à chaque modification.
    currency: input.currency ?? "USD",
    frequency: input.frequency,
    ...(input.renewalDate ? { renewalDate: input.renewalDate } : {}),
    ...(input.accountId ? { accountId: input.accountId } : {}),
    status: input.status ?? "ACTIVE",
  };
}
